'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import posthog from 'posthog-js';
import MicrophonePermissionModal, {
  type MicrophonePermissionState,
} from '@/components/permissions/MicrophonePermissionModal';
import { BoardCanvas } from '@/components/whiteboard-v2/BoardCanvas';
import {
  createV2ToolDefinitions,
  type TranscriptionToolHandler,
  V2_HISTORY_INITIAL_MESSAGE,
  V2_INITIAL_MESSAGE,
  V2_INTERACTIVE_CONCEPT_INITIAL_MESSAGE,
} from '@/components/whiteboard-v2/whiteboardV2LiveMode';
import type { BoardEntry, StoredProblem } from '@/components/whiteboard-v2/types';
import {
  TUTOR_CHARACTERS,
  DEFAULT_CHARACTER_ID,
} from '@/components/whiteboard-v2/tutorPersonalities';
import { CharacterPicker } from '@/components/whiteboard-v2/CharacterPicker';
import {
  LiveModeOverlay,
  useLiveMode,
  type LiveModeSessionOptions,
  type LiveModeStatus,
  type ProblemContext,
} from '@/components/tutor/live-mode';
import useAuthStore from '@/store/useAuthStore';
import Image from '@/components/Image';
import { isLearnTest } from '@/lib/utils';
import { normalizeLiveTutorSubject } from '@/lib/liveTutorSubject';
import type { LessonReviewContext } from '@/types/review-live';

type DiagramMode = 'auto' | 'coordinate_plane' | 'geometry';

const CHARACTER_BOARD_THEMES: Record<string, string> = {
  ada: 'board-theme-ada',
  wizard: 'board-theme-wizard',
  pirate: 'board-theme-pirate',
  'sports-commentator': 'board-theme-sports-commentator',
  detective: 'board-theme-detective',
  'mad-scientist': 'board-theme-mad-scientist',
  'anime-sensei': 'board-theme-anime-sensei',
  comedian: 'board-theme-comedian',
};

interface KeyPropertyPayload {
  id: string;
  title: string;
  content: string;
  examples?: string;
  explanation?: string;
  picture?: string;
  picturePosition?: 'below' | 'right';
}

export type DemoSelection =
  | { kind: 'problem'; problem: StoredProblem }
  | {
      kind: 'skill';
      skillId: string;
      keyProperty: KeyPropertyPayload;
      problem: StoredProblem;
    }
  | {
      kind: 'review';
      review: LessonReviewContext;
    };

export interface LiveWhiteboardProps {
  /** The current selection to show on the whiteboard. null = blank board. */
  selection: DemoSelection | null;
  /** Parent-level gate for guest users (e.g. only allow one free session). */
  isGuestSessionLocked?: boolean;
  /** Called when the user needs to sign in (e.g. exceeded free session limit). */
  onRequireAuth?: () => void;
  /** Called when the live session ends. */
  onSessionEnd?: () => void;
  /** Called when the live session starts (or attempts start). */
  onSessionStart?: () => void;
  /** Called when the current problem is fully completed on whiteboard. */
  onProblemCompleted?: () => void;
  /** Pre-generated board entries to show as a preview before starting a live session. */
  previewEntries?: BoardEntry[];
  /** Optional persisted board header visibility. */
  previewShowBoardHeader?: boolean;
  /** Optional persisted star state. */
  previewStarStates?: Array<'pending' | 'success' | 'fail'>;
  /** When true, automatically start the live session when selection changes. */
  autoStart?: boolean;
  /** Increment this value to explicitly start the live session for the current selection. */
  startSessionSignal?: number;
  /** Optional externally persisted start-signal watermark to avoid duplicate starts across remounts. */
  consumedStartSessionSignal?: number;
  /** Called when a startSessionSignal value is consumed. */
  onConsumeStartSessionSignal?: (signal: number) => void;
  /** Optional session overrides for special entry points like Ask Pengi live. */
  liveSessionOptions?: Pick<LiveModeSessionOptions, 'buildSystemPrompt' | 'initialMessage' | 'onStableTranscription' | 'onTranscription'> & {
    followUpMessage?: string;
    transcriptionToolDefinitions?: TranscriptionToolHandler[];
  };
  /** Optional context overrides for special entry points like Ask Pengi live. */
  contextOverrides?: Partial<ProblemContext>;
  /** Called when the whiteboard snapshot changes so parents can persist it. */
  onBoardStateChange?: (state: {
    entries: BoardEntry[];
    showBoardHeader: boolean;
    starStates: Array<'pending' | 'success' | 'fail'>;
  }) => void;
  /** Override the default desktop whiteboard width (default: 820). */
  desktopWidth?: number;
  /** Override the default desktop whiteboard height (default: 560). */
  desktopHeight?: number;
}

export default function LiveWhiteboard({
  selection,
  isGuestSessionLocked = false,
  onRequireAuth,
  onSessionEnd,
  onSessionStart,
  onProblemCompleted,
  previewEntries,
  previewShowBoardHeader = true,
  previewStarStates,
  autoStart = false,
  startSessionSignal,
  consumedStartSessionSignal,
  onConsumeStartSessionSignal,
  liveSessionOptions,
  contextOverrides,
  onBoardStateChange,
  desktopWidth = 820,
  desktopHeight = 560,
}: LiveWhiteboardProps) {
  const user = useAuthStore((s) => s.user);
  const liveMode = useLiveMode();
  const liveModeStatus = liveMode.status;
  const endLiveModeSession = liveMode.endSession;
  const reduceMotion = useReducedMotion();
  const [selectedCharacterId, setSelectedCharacterId] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_CHARACTER_ID;
    try {
      const stored = localStorage.getItem('pengi_live_tutor_character');
      if (stored && TUTOR_CHARACTERS.some((c) => c.personalityId === stored)) return stored;
    } catch {}
    return DEFAULT_CHARACTER_ID;
  });

  const selectedCharacterIdRef = useRef(selectedCharacterId);

  const handleCharacterChange = useCallback((personalityId: string) => {
    setSelectedCharacterId(personalityId);
    selectedCharacterIdRef.current = personalityId;
    try { localStorage.setItem('pengi_live_tutor_character', personalityId); } catch {}
  }, []);

  const [isGenerating, setIsGenerating] = useState(false);
  const [boardEntries, setBoardEntries] = useState<BoardEntry[]>(previewEntries ?? []);
  const [showBoardHeader, setShowBoardHeader] = useState(previewShowBoardHeader);
  const [starStates, setStarStates] = useState<
    Array<'pending' | 'success' | 'fail'>
  >(previewStarStates ?? ['pending', 'pending', 'pending']);
  const [highlightStarIndex, setHighlightStarIndex] = useState<number | null>(
    null,
  );
  const [hasClickedStartLiveTutor, setHasClickedStartLiveTutor] =
    useState(false);
  const [isStartingLiveTutor, setIsStartingLiveTutor] = useState(false);
  const [isMicPermissionModalOpen, setIsMicPermissionModalOpen] =
    useState(false);
  const [micPermissionState, setMicPermissionState] =
    useState<MicrophonePermissionState>('unknown');
  const [isRequestingMicPermission, setIsRequestingMicPermission] =
    useState(false);
  const [micPermissionError, setMicPermissionError] = useState<string | null>(
    null,
  );
  const [viewportWidth, setViewportWidth] = useState(1200);
  const [viewportHeight, setViewportHeight] = useState(800);
  const [diagramMode, setDiagramMode] = useState<DiagramMode>('auto');
  const [isDiagramPending, setIsDiagramPending] = useState(false);

  const getBoardContentsRef = useRef<(() => string[]) | null>(null);
  const boardSessionEpochRef = useRef(0);
  const pendingLiveStartActionRef = useRef<(() => Promise<void>) | null>(null);
  const previousLiveStatusRef = useRef(liveMode.status);
  const whiteboardSectionRef = useRef<HTMLDivElement>(null);
  const hasReportedProblemCompletedRef = useRef(false);
  const diagramDecisionCacheRef = useRef<Map<string, DiagramMode>>(new Map());
  const diagramDecisionEpochRef = useRef(0);
  const lastStartSessionSignalRef = useRef(startSessionSignal);
  const lastAlertedLiveErrorRef = useRef<string | null>(null);
  const onBoardStateChangeRef = useRef(onBoardStateChange);
  const lastFollowUpMessageRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const startRequestTokenRef = useRef(0);

  useEffect(() => {
    onBoardStateChangeRef.current = onBoardStateChange;
  }, [onBoardStateChange]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      startRequestTokenRef.current += 1;
    };
  }, []);

  // ─── derived ───────────────────────────────────────────────────
  const effectiveLiveModeStatus: LiveModeStatus =
    isStartingLiveTutor && liveMode.status === 'inactive'
      ? 'connecting'
      : liveMode.status;
  const isLiveActive = effectiveLiveModeStatus !== 'inactive';
  const isMobileWhiteboardLayout = viewportWidth < 768;
  const isLiveOnMobile = isLiveActive && isMobileWhiteboardLayout;
  const shouldCenterLiveStartButton =
    effectiveLiveModeStatus === 'inactive' && !hasClickedStartLiveTutor;
  const boardThemeClassName =
    CHARACTER_BOARD_THEMES[selectedCharacterId] ?? 'board-theme-ada';

  const whiteboardCanvasSize = useMemo(() => {
    if (viewportWidth < 768) {
      if (isLiveOnMobile) {
        const framePadding = 28;
        const width = viewportWidth - framePadding;
        const overlaySpace = 72;
        const height = viewportHeight - framePadding - overlaySpace;
        return { width, height, fontScale: 0.8 };
      }
      const horizontalPadding = 32;
      const width = Math.max(
        280,
        Math.min(420, viewportWidth - horizontalPadding),
      );
      const height = Math.round(width * 1.08);
      return { width, height, fontScale: 0.8 };
    }
    return { width: desktopWidth, height: desktopHeight, fontScale: 1 };
  }, [viewportWidth, viewportHeight, isLiveOnMobile, desktopWidth, desktopHeight]);

  // ─── board header derived values ──────────────────────────────
  const boardQuestion =
    selection?.kind === 'problem' && showBoardHeader
      ? selection.problem.question || ''
      : undefined;
  const boardQuestionHtml =
    selection?.kind === 'problem' && showBoardHeader
      ? selection.problem.questionHtml
      : undefined;
  const boardOptions =
    selection?.kind === 'problem' && showBoardHeader
      ? selection.problem.options
      : undefined;
  const boardQuestionImage =
    selection?.kind === 'problem' && showBoardHeader
      ? selection.problem.imageUrl
      : undefined;
  const boardHeaderTitle =
    selection?.kind === 'skill' && showBoardHeader
      ? selection.keyProperty.title
      : selection?.kind === 'review' && showBoardHeader
        ? selection.review.reviewPlan[selection.review.currentStepIndex]?.title || selection.review.lessonTitle
      : undefined;
  const boardHeaderImage =
    selection?.kind === 'skill' && showBoardHeader
      ? selection.keyProperty.picture
      : selection?.kind === 'review' && showBoardHeader
        ? selection.review.reviewPlan[selection.review.currentStepIndex]?.picture
      : undefined;
  const boardHeaderImagePosition =
    selection?.kind === 'skill'
      ? selection.keyProperty.picturePosition
      : selection?.kind === 'review'
        ? selection.review.reviewPlan[selection.review.currentStepIndex]?.picturePosition
      : undefined;

  // ─── helpers ──────────────────────────────────────────────────
  const resetBoardState = useCallback(() => {
    setBoardEntries([]);
    setShowBoardHeader(true);
    setStarStates(['pending', 'pending', 'pending']);
    setHighlightStarIndex(null);
    getBoardContentsRef.current = null;
    hasReportedProblemCompletedRef.current = false;
  }, []);

  const resolveDiagramMode = useCallback(
    async (sel: DemoSelection): Promise<DiagramMode> => {
      if (sel.kind !== 'problem') {
        setIsDiagramPending(false);
        setDiagramMode('auto');
        return 'auto';
      }

      const problem = sel.problem;
      const cacheKey = problem.id || '';
      if (cacheKey && diagramDecisionCacheRef.current.has(cacheKey)) {
        const cached = diagramDecisionCacheRef.current.get(cacheKey) ?? 'auto';
        setIsDiagramPending(false);
        setDiagramMode(cached);
        return cached;
      }

      const question = problem.question?.trim() ?? '';
      if (!question) {
        setIsDiagramPending(false);
        setDiagramMode('auto');
        return 'auto';
      }

      const epoch = ++diagramDecisionEpochRef.current;
      setIsDiagramPending(true);

      try {
        const response = await fetch('/napi/diagram/decision', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            question,
            options: problem.options ?? [],
            hasImage: Boolean(problem.imageUrl || problem.imageUrls?.length),
          }),
        });
        const data = (await response.json()) as { mode?: DiagramMode };
        const mode: DiagramMode =
          data.mode === 'coordinate_plane' || data.mode === 'geometry' || data.mode === 'auto'
            ? data.mode
            : 'auto';

        if (cacheKey) {
          diagramDecisionCacheRef.current.set(cacheKey, mode);
        }
        if (diagramDecisionEpochRef.current === epoch) {
          setDiagramMode(mode);
          setIsDiagramPending(false);
        }
        return mode;
      } catch (error) {
        console.warn('[LiveWhiteboard] Diagram decision failed, fallback to auto:', error);
        if (diagramDecisionEpochRef.current === epoch) {
          setDiagramMode('auto');
          setIsDiagramPending(false);
        }
        return 'auto';
      }
    },
    [],
  );

  const focusWhiteboardSection = useCallback(() => {
    const section = whiteboardSectionRef.current;
    if (!section) return;
    const behavior: ScrollBehavior = reduceMotion ? 'auto' : 'smooth';
    const scrollToSection = () => {
      const top = section.getBoundingClientRect().top + window.scrollY - 12;
      window.scrollTo({ top: Math.max(0, top), behavior });
    };
    scrollToSection();
    window.setTimeout(scrollToSection, reduceMotion ? 0 : 260);
  }, [reduceMotion]);

  // ─── mic permission ───────────────────────────────────────────
  const resolveMicPermissionState = useCallback(
    async (): Promise<MicrophonePermissionState> => {
      if (
        !window.isSecureContext &&
        window.location.hostname !== 'localhost'
      ) {
        return 'unsupported';
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        return 'unsupported';
      }
      try {
        if (navigator.permissions?.query) {
          const result = await navigator.permissions.query({
            name: 'microphone' as PermissionName,
          });
          return result.state;
        }
      } catch {
        // Permissions API not fully supported
      }
      return 'unknown';
    },
    [],
  );

  const promptForMicrophonePermission = useCallback(async (action: () => Promise<void>) => {
    const permission = await resolveMicPermissionState();
    if (permission === 'granted') {
      await action();
      return;
    }
    pendingLiveStartActionRef.current = action;
    setMicPermissionError(null);
    setMicPermissionState(permission);
    setIsMicPermissionModalOpen(true);
  }, [resolveMicPermissionState]);

  const closeMicrophonePermissionModal = () => {
    setIsMicPermissionModalOpen(false);
    setMicPermissionError(null);
    setIsRequestingMicPermission(false);
    pendingLiveStartActionRef.current = null;
    // Reset starting state so fullscreen overlay doesn't get stuck
    setHasClickedStartLiveTutor(false);
    setIsStartingLiveTutor(false);
  };

  const handleRequestMicrophonePermission = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicPermissionState('unsupported');
      setMicPermissionError(
        'This browser does not support microphone access.',
      );
      return;
    }
    setIsRequestingMicPermission(true);
    setMicPermissionError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setMicPermissionState('granted');
      setIsMicPermissionModalOpen(false);
      const pendingAction = pendingLiveStartActionRef.current;
      pendingLiveStartActionRef.current = null;
      if (pendingAction) {
        await pendingAction();
      }
    } catch (error) {
      if (error instanceof DOMException) {
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'PermissionDeniedError'
        ) {
          setMicPermissionError(
            'Microphone permission was denied. Please enable it in browser settings and retry.',
          );
        } else if (
          error.name === 'NotFoundError' ||
          error.name === 'DevicesNotFoundError'
        ) {
          setMicPermissionError(
            'No microphone was found. Please connect one and retry.',
          );
        } else {
          setMicPermissionError(
            error.message || 'Failed to request microphone permission.',
          );
        }
      } else {
        setMicPermissionError('Failed to request microphone permission.');
      }
      const nextPermission = await resolveMicPermissionState();
      setMicPermissionState(nextPermission);
    } finally {
      setIsRequestingMicPermission(false);
    }
  };

  // ─── live session lifecycle ───────────────────────────────────
  const startLiveDemo = useCallback(
    async (selectionOverride?: DemoSelection): Promise<boolean> => {
      const startToken = startRequestTokenRef.current;
      if (isGuestSessionLocked && !user) {
        onRequireAuth?.();
        return false;
      }
      if (!selectionOverride && liveMode.status !== 'inactive') {
        return false;
      }

      const sel = selectionOverride ?? selection;
      if (!sel) return false;

      const resolvedDiagramMode = await resolveDiagramMode(sel);
      if (!isMountedRef.current || startRequestTokenRef.current !== startToken) {
        return false;
      }

      setIsGenerating(true);
      resetBoardState();
      if (!selectionOverride && previewEntries && previewEntries.length > 0) {
        setBoardEntries(previewEntries);
        setShowBoardHeader(previewShowBoardHeader);
      }

      const epoch = ++boardSessionEpochRef.current;
      const appendEntry = (entry: BoardEntry) => {
        if (boardSessionEpochRef.current !== epoch) return;
        setBoardEntries((prev) => [...prev, entry]);
      };
      const updateEntry = (id: string, next: BoardEntry) => {
        if (boardSessionEpochRef.current !== epoch) return;
        setBoardEntries((prev) => prev.map((entry) => (entry.id === id ? next : entry)));
      };
      const clearEntries = () => {
        if (boardSessionEpochRef.current !== epoch) return;
        setBoardEntries([]);
      };
      const clearHeader = () => {
        if (boardSessionEpochRef.current !== epoch) return;
        setShowBoardHeader(false);
      };
      const updateStarState = (
        index: number,
        state: 'pending' | 'success' | 'fail',
      ) => {
        setStarStates((prev) => {
          const next = [...prev];
          next[index] = state;
          return next;
        });
      };
      const selectedCharacter = TUTOR_CHARACTERS.find(
        (c) => c.personalityId === selectedCharacterIdRef.current,
      ) ?? TUTOR_CHARACTERS[0];
      const voiceDisplayName = selectedCharacter.name;

      let context: ProblemContext;
      let toolDefinitions: ReturnType<typeof createV2ToolDefinitions>['tools'];
      let initialMessage: string;
      let onTranscription: (text: string, isUser: boolean) => void;

      if (sel.kind === 'skill') {
        const keyProperty = sel.keyProperty;
        const { tools, getBoardContents } = createV2ToolDefinitions(
          appendEntry,
          clearEntries,
          clearHeader,
          undefined,
          undefined,
          keyProperty.title,
          updateEntry,
        );
        toolDefinitions = tools;
        getBoardContentsRef.current = getBoardContents;

        context = {
          elementId: keyProperty.id,
          skillId: sel.skillId,
          question: keyProperty.title,
          subject: normalizeLiveTutorSubject(sel.problem.subject),
          contextType: 'key_property',
          keyProperty: {
            title: keyProperty.title,
            content: keyProperty.content,
            examples: keyProperty.examples,
            explanation: keyProperty.explanation,
          },
        };
        initialMessage = V2_INTERACTIVE_CONCEPT_INITIAL_MESSAGE;
        onTranscription = () => {};
      } else if (sel.kind === 'review') {
        const currentStep = sel.review.reviewPlan[sel.review.currentStepIndex] ?? sel.review.reviewPlan[0];
        const { tools, getBoardContents } = createV2ToolDefinitions(
          appendEntry,
          clearEntries,
          clearHeader,
          undefined,
          undefined,
          currentStep?.title || sel.review.lessonTitle,
          updateEntry,
        );
        toolDefinitions = tools;
        getBoardContentsRef.current = getBoardContents;

        context = {
          elementId: sel.review.lessonId,
          skillId: currentStep?.skillId,
          question: currentStep?.title || sel.review.lessonTitle,
          subject: normalizeLiveTutorSubject(sel.review.subject),
          contextType: 'lesson_review',
          questTitle: sel.review.questTitle,
          chapterTitle: sel.review.chapterTitle,
          lessonTitle: sel.review.lessonTitle,
          contextSummary: sel.review.contextSummary,
          reviewPlan: sel.review.reviewPlan,
          currentStepIndex: sel.review.currentStepIndex,
        };
        initialMessage = "I want to review this lesson one key idea at a time. Start with the first key property, ask me to explain it, and only give hints before teaching.";
        onTranscription = () => {};
      } else {
        const problem = sel.problem;
        if (!problem) return false;
        const { tools, getBoardContents } = createV2ToolDefinitions(
          appendEntry,
          clearEntries,
          clearHeader,
          updateStarState,
          setHighlightStarIndex,
          problem.question || '',
          updateEntry,
        );
        toolDefinitions = tools;
        getBoardContentsRef.current = getBoardContents;

        context = {
          elementId: problem.id,
          skillId: problem.skillId,
          question: problem.question || '',
          answer: problem.answer,
          explanation: problem.explanation,
          subject: normalizeLiveTutorSubject(problem.subject),
          contextType: 'question',
          options: problem.options,
        };
        console.info('[LiveTutor][model] starting question context', {
          elementId: context.elementId,
          contextType: context.contextType,
          question: context.question,
          options: problem.options,
          questionHtmlPreview: problem.questionHtml?.slice(0, 500),
          imageUrl: problem.imageUrl,
          imageUrls: problem.imageUrls,
          sourceType: problem.sourceType,
        });
        initialMessage = context.subject === 'history'
          ? V2_HISTORY_INITIAL_MESSAGE
          : V2_INITIAL_MESSAGE;
        onTranscription = () => {};
      }

      try {
        if (!isMountedRef.current || startRequestTokenRef.current !== startToken) {
          return false;
        }
        const character = TUTOR_CHARACTERS.find((c) => c.personalityId === selectedCharacterIdRef.current) ?? TUTOR_CHARACTERS[0];
        const started = await liveMode.startSession({ ...context, ...contextOverrides }, {
          voiceName: character.voiceId,
          personalityId: character.personalityId,
          diagramMode: resolvedDiagramMode,
          transcriptionToolDefinitions: [
            ...toolDefinitions,
            ...(liveSessionOptions?.transcriptionToolDefinitions ?? []),
          ],
          buildSystemPrompt: liveSessionOptions?.buildSystemPrompt,
          initialMessage: liveSessionOptions?.initialMessage ?? initialMessage,
          initialTurnComplete: true,
          onTranscription: (text, isUser, source) => {
            onTranscription(text, isUser);
            liveSessionOptions?.onTranscription?.(text, isUser, source);
          },
          onStableTranscription: liveSessionOptions?.onStableTranscription,
          getReconnectContext: () => {
            const board = getBoardContentsRef.current?.();
            if (!board || board.length === 0) return undefined;
            return `The whiteboard currently shows these items (do NOT re-write any of them):\n${board.map((item) => `- ${item}`).join('\n')}`;
          },
        });
        return started;
      } catch (error) {
        console.error('Failed to start live demo:', error);
        return false;
      } finally {
        if (isMountedRef.current && startRequestTokenRef.current === startToken) {
          setIsGenerating(false);
        }
      }
    },
    [
      user,
      isGuestSessionLocked,
      onRequireAuth,
      liveMode,
      selection,
      liveSessionOptions,
      contextOverrides,
      resolveDiagramMode,
      resetBoardState,
      previewEntries,
      previewShowBoardHeader,
    ],
  );

  const endLiveDemo = useCallback(() => {
    console.info('[LiveWhiteboard] endLiveDemo called');
    liveMode.endSession('manual_end_live_demo');
  }, [liveMode]);

  const handleOverlayStartSession = useCallback(async () => {
    const startToken = ++startRequestTokenRef.current;
    setHasClickedStartLiveTutor(true);
    setIsStartingLiveTutor(true);
    resetBoardState();
    focusWhiteboardSection();
    await promptForMicrophonePermission(async () => {
      if (!isMountedRef.current || startRequestTokenRef.current !== startToken) {
        return;
      }
      const started = await startLiveDemo(selection ?? undefined);
      if (!started) {
        if (!isMountedRef.current || startRequestTokenRef.current !== startToken) {
          return;
        }
        setHasClickedStartLiveTutor(false);
        setIsStartingLiveTutor(false);
        return;
      }
      onSessionStart?.();
    });
  }, [focusWhiteboardSection, onSessionStart, promptForMicrophonePermission, resetBoardState, selection, startLiveDemo]);

  // Reset board when selection changes
  // Auto-start live session if autoStart is enabled and user changed selection
  const prevSelectionRef = useRef(selection);
  const isInitialSelectionRef = useRef(true);
  useEffect(() => {
    if (prevSelectionRef.current !== selection) {
      const isInitialSelection = isInitialSelectionRef.current;
      prevSelectionRef.current = selection;
      resetBoardState();
      setHasClickedStartLiveTutor(false);
      setIsStartingLiveTutor(false);
      if (selection) {
        void resolveDiagramMode(selection);
      } else {
        setDiagramMode('auto');
        setIsDiagramPending(false);
      }
      if (liveModeStatus !== 'inactive') {
        console.info('[LiveWhiteboard] Ending live session because selection changed', {
          nextKind: selection?.kind ?? null,
        });
        endLiveModeSession('selection_changed');
      }
      if (isInitialSelection && previewEntries) {
        setBoardEntries(previewEntries);
        setShowBoardHeader(previewShowBoardHeader);
      }
      if (autoStart && selection && !isInitialSelection) {
        startSessionRef.current(selection);
      }
      isInitialSelectionRef.current = false;
    }
  }, [selection, resetBoardState, liveModeStatus, endLiveModeSession, autoStart, previewEntries, previewShowBoardHeader, resolveDiagramMode]);

  useEffect(() => {
    if (!previewEntries) return;
    setBoardEntries(previewEntries);
    setShowBoardHeader(previewShowBoardHeader);
  }, [previewEntries, previewShowBoardHeader]);

  useEffect(() => {
    onBoardStateChangeRef.current?.({
      entries: boardEntries,
      showBoardHeader,
      starStates,
    });
  }, [boardEntries, showBoardHeader, starStates]);

  // Track live mode status changes
  useEffect(() => {
    if (
      previousLiveStatusRef.current !== 'inactive' &&
      liveMode.status === 'inactive'
    ) {
      setHasClickedStartLiveTutor(false);
      setIsStartingLiveTutor(false);
      onSessionEnd?.();
    }
    if (liveMode.status !== 'inactive') {
      setIsStartingLiveTutor(false);
    }
    previousLiveStatusRef.current = liveMode.status;
  }, [liveMode.status, onSessionEnd]);

  useEffect(() => {
    if (!liveMode.error) {
      lastAlertedLiveErrorRef.current = null;
      return;
    }

    if (lastAlertedLiveErrorRef.current === liveMode.error) {
      return;
    }

    lastAlertedLiveErrorRef.current = liveMode.error;
    window.alert(liveMode.error);
  }, [liveMode.error]);

  // A problem is considered complete when all 3 checkpoint stars are awarded.
  useEffect(() => {
    if (!selection || selection.kind !== 'problem') return;
    if (hasReportedProblemCompletedRef.current) return;
    if (starStates.length !== 3) return;
    if (!starStates.every((state) => state === 'success')) return;

    hasReportedProblemCompletedRef.current = true;
    onProblemCompleted?.();
  }, [onProblemCompleted, selection, starStates]);

  // Viewport resize tracking
  useEffect(() => {
    const updateViewportSize = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };
    updateViewportSize();
    window.addEventListener('resize', updateViewportSize);
    return () => window.removeEventListener('resize', updateViewportSize);
  }, []);

  // Prevent background page from scrolling when whiteboard is fullscreen on mobile
  useEffect(() => {
    if (!isLiveOnMobile) return;
    const html = document.documentElement;
    const { body } = document;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
    };
  }, [isLiveOnMobile]);

  // ─── public method: start a session with the given selection ───
  // This is called from parent when selecting a problem/skill.
  const startSessionForSelection = useCallback(
    async (sel: DemoSelection) => {
      const startToken = ++startRequestTokenRef.current;
      setHasClickedStartLiveTutor(true);
      setIsStartingLiveTutor(true);
      focusWhiteboardSection();
      resetBoardState();
      await promptForMicrophonePermission(async () => {
        if (!isMountedRef.current || startRequestTokenRef.current !== startToken) {
          return;
        }
        const started = await startLiveDemo(sel);
        if (started) {
          onSessionStart?.();
          return;
        }
        if (!isMountedRef.current || startRequestTokenRef.current !== startToken) {
          return;
        }
        setHasClickedStartLiveTutor(false);
        setIsStartingLiveTutor(false);
      });
    },
    [focusWhiteboardSection, onSessionStart, promptForMicrophonePermission, resetBoardState, startLiveDemo],
  );

  // Expose the start function via ref
  // We store it so the parent can call it.
  const startSessionRef = useRef(startSessionForSelection);
  startSessionRef.current = startSessionForSelection;

  useEffect(() => {
    if (lastStartSessionSignalRef.current === startSessionSignal) {
      return;
    }

    if (
      typeof startSessionSignal === 'number'
      && typeof consumedStartSessionSignal === 'number'
      && startSessionSignal <= consumedStartSessionSignal
    ) {
      lastStartSessionSignalRef.current = startSessionSignal;
      return;
    }

    lastStartSessionSignalRef.current = startSessionSignal;

    if (!selection) {
      return;
    }

    if (typeof startSessionSignal === 'number') {
      onConsumeStartSessionSignal?.(startSessionSignal);
    }

    startSessionRef.current(selection);
  }, [selection, startSessionSignal, consumedStartSessionSignal, onConsumeStartSessionSignal]);

  useEffect(() => {
    const followUpMessage = liveSessionOptions?.followUpMessage?.trim();
    if (!followUpMessage || liveMode.status !== 'active') {
      return;
    }

    if (lastFollowUpMessageRef.current === followUpMessage) {
      return;
    }

    lastFollowUpMessageRef.current = followUpMessage;
    liveMode.sendClientContent(followUpMessage, true);
  }, [liveMode, liveMode.status, liveSessionOptions?.followUpMessage]);

  // ─── render ───────────────────────────────────────────────────
  return (
    <>
      <section
        ref={whiteboardSectionRef}
        className={`${isLiveOnMobile ? 'fixed inset-0 z-[80] pb-0 overflow-hidden' : 'relative pb-0'}`}
      >
        <div
          className={`mx-auto w-full ${isLiveOnMobile ? 'h-full px-0' : 'max-w-[1200px] px-4 sm:px-6 md:px-8'}`}
        >
          <AnimatePresence>
            {selection && (
              <motion.div
                key="whiteboard-live"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{
                  duration: reduceMotion ? 0 : 0.4,
                  ease: 'easeOut',
                }}
                className={
                  isLiveOnMobile
                    ? 'overflow-visible h-full'
                    : 'overflow-visible'
                }
              >
                <div
                  className={`relative flex justify-center ${
                    isLiveOnMobile
                      ? 'flex-col items-center h-full px-0 pt-0 pb-0'
                      : isMobileWhiteboardLayout
                        ? 'flex-col items-center overflow-visible px-0 pt-2 pb-3'
                        : 'overflow-x-clip overflow-y-visible px-1 pt-3 pb-6 sm:px-0 md:pt-4'
                  }`}
                >
                  <div className="relative" style={{ zIndex: 1 }}>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: reduceMotion ? 0 : 0.35,
                        delay: 0.1,
                      }}
                      className={`home-chalkboard-frame overflow-hidden relative ${boardThemeClassName}`}
                    >
                      <div className="relative">
                        <BoardCanvas
                          entries={boardEntries}
                          width={whiteboardCanvasSize.width}
                          height={whiteboardCanvasSize.height}
                          fontScale={whiteboardCanvasSize.fontScale}
                          compactMode={isMobileWhiteboardLayout}
                          question={boardQuestion}
                          questionHtml={boardQuestionHtml}
                          options={boardOptions}
                          questionImage={boardQuestionImage}
                          headerTitle={boardHeaderTitle}
                          headerImage={boardHeaderImage}
                          headerImagePosition={boardHeaderImagePosition}
                          starStates={starStates}
                          highlightStarIndex={highlightStarIndex}
                          onStarStatesChange={setStarStates}
                          className={`border-0 shadow-none ${boardThemeClassName}`}
                        />
                        {isLearnTest && (
                          <div className="absolute right-4 top-3 z-20">
                            <span
                              className={[
                                'inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide border border-white/40',
                                isDiagramPending
                                  ? 'text-[#e6dcc0] opacity-70'
                                  : 'text-[#f3ecd7] opacity-90',
                              ].join(' ')}
                            >
                              <span
                                className={[
                                  'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
                                  isDiagramPending
                                    ? 'bg-[#e6dcc0] animate-pulse opacity-80'
                                    : 'bg-[#6de2ad] opacity-95',
                                ].join(' ')}
                              />
                              {isDiagramPending ? 'pending' : diagramMode === 'coordinate_plane' ? 'coord' : diagramMode === 'geometry' ? 'geo' : 'auto'}
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                    {/* Live mode overlay: center before starting */}
                    {!isMobileWhiteboardLayout ||
                    shouldCenterLiveStartButton ? (
                      <div
                        className={
                          shouldCenterLiveStartButton
                            ? 'absolute inset-0 z-10 flex items-center justify-center pointer-events-none'
                            : 'absolute bottom-0 left-1/2 z-10 -translate-x-1/2 translate-y-[calc(25%+15px)]'
                        }
                      >
                        <div
                          className={`${shouldCenterLiveStartButton ? 'pointer-events-auto' : ''} origin-bottom scale-[0.78] sm:scale-[0.92] md:scale-100`}
                        >
                          <LiveModeOverlay
                            status={effectiveLiveModeStatus}
                            isModelSpeaking={liveMode.isModelSpeaking}
                            onStartSession={handleOverlayStartSession}
                            onEndSession={endLiveDemo}

                            sessionWarningAt={liveMode.sessionWarningAt}
                            speakerIcon={
                              <Image
                                src={(TUTOR_CHARACTERS.find((c) => c.personalityId === selectedCharacterId) ?? TUTOR_CHARACTERS[0]).avatar}
                                alt=""
                                width={40}
                                height={40}
                                optimizedWidth={200}
                                className="w-[40px] h-[40px] object-cover rounded-full"
                              />
                            }
                            settingsTrigger={
                              <Image
                                src={(TUTOR_CHARACTERS.find((c) => c.personalityId === selectedCharacterId) ?? TUTOR_CHARACTERS[0]).avatar}
                                alt={(TUTOR_CHARACTERS.find((c) => c.personalityId === selectedCharacterId) ?? TUTOR_CHARACTERS[0]).name}
                                width={36}
                                height={36}
                                optimizedWidth={200}
                                className="w-full h-full object-cover"
                              />
                            }
                            settingsSlot={(close) => (
                              <CharacterPicker
                                selectedId={selectedCharacterId}
                                onSelect={handleCharacterChange}
                                onClose={close}
                              />
                            )}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                  {isMobileWhiteboardLayout &&
                  !shouldCenterLiveStartButton ? (
                    <div
                      className={`z-10 flex justify-center ${isLiveOnMobile ? 'fixed bottom-4 left-0 right-0' : 'relative mt-4'}`}
                    >
                      <div
                        className={
                          isLiveOnMobile ? '' : 'origin-top scale-[0.9]'
                        }
                      >
                        <LiveModeOverlay
                          status={effectiveLiveModeStatus}
                          isModelSpeaking={liveMode.isModelSpeaking}
                          onStartSession={handleOverlayStartSession}
                          onEndSession={endLiveDemo}
                          sessionWarningAt={liveMode.sessionWarningAt}
                          speakerIcon={
                            <Image
                              src={(TUTOR_CHARACTERS.find((c) => c.personalityId === selectedCharacterId) ?? TUTOR_CHARACTERS[0]).avatar}
                              alt=""
                              width={40}
                              height={40}
                              optimizedWidth={200}
                              className="w-[40px] h-[40px] object-cover rounded-full"
                            />
                          }
                          settingsTrigger={
                            <Image
                              src={(TUTOR_CHARACTERS.find((c) => c.personalityId === selectedCharacterId) ?? TUTOR_CHARACTERS[0]).avatar}
                              alt={(TUTOR_CHARACTERS.find((c) => c.personalityId === selectedCharacterId) ?? TUTOR_CHARACTERS[0]).name}
                              width={36}
                              height={36}
                              optimizedWidth={200}
                              className="w-full h-full object-cover"
                            />
                          }
                          settingsSlot={(close) => (
                            <CharacterPicker
                              selectedId={selectedCharacterId}
                              onSelect={handleCharacterChange}
                              onClose={close}
                            />
                          )}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Session timeout end card */}
        {liveMode.isTimeoutEnded && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-xl">
            <div className="bg-white rounded-2xl shadow-2xl px-8 py-8 max-w-sm w-full mx-4 text-center flex flex-col gap-4">
              <div className="text-2xl font-bold text-gray-900">Session ended</div>
              <p className="text-gray-500 text-sm leading-relaxed">
                Great work today! Start a new session anytime to keep going.
              </p>
              <button
                type="button"
                onClick={liveMode.resetTimeoutEnd}
                className="mt-2 w-full rounded-full bg-[color:var(--primary-500)] py-3 text-white font-semibold text-sm hover:bg-[color:var(--primary-600)] transition-colors"
              >
                Start New Session
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Microphone permission modal */}
      <MicrophonePermissionModal
        open={isMicPermissionModalOpen}
        onClose={closeMicrophonePermissionModal}
        permissionState={micPermissionState}
        onRequestPermission={handleRequestMicrophonePermission}
        isRequesting={isRequestingMicPermission}
        errorMessage={micPermissionError}
      />
    </>
  );
}
